import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Chatbot from "./components/Chatbot";
import { useAuth, useCart, useWishlistLocal, apiFetch } from "./lib/api";
import {
  Home,
  Category,
  ProductDetail,
  Checkout,
  About,
  Contact,
  StaticPage,
} from "./pages/Public";
import {
  Login,
  Signup,
  ForgotPassword,
  ResetPassword,
  VerifyEmail,
  AuthCallback,
  Account,
} from "./pages/Auth";
import AdminApp from "./pages/Admin";
import "@/App.css";

function Shell() {
  const auth = useAuth();
  const cart = useCart();
  const wish = useWishlistLocal();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  // Sync wishlist to server when signed in; merge local → server once on login
  useEffect(() => {
    if (!auth.user) return;

    (async () => {
      try {
        const serverWish = await apiFetch("/wishlist");
        const serverIds = serverWish.map((p) => p.id);

        for (const id of wish.wishlist) {
          if (!serverIds.includes(id)) {
            await apiFetch(`/wishlist/${id}`, {
              method: "POST",
            }).catch(() => {});
          }
        }
      } catch {}
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user?.user_id]);

  if (auth.loading && !location.hash.includes("session_id=")) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div className="loader-mark" aria-hidden>
          ✦
        </div>
        <p>GMK Handicrafts Emporium</p>
      </div>
    );
  }

  return (
    <>
      {!isAdminRoute && (
        <Header
          user={auth.user}
          cartCount={cart.count}
          wishCount={wish.wishlist.length}
          onLogout={auth.logout}
        />
      )}

      <Routes>
        {/* Homepage */}
        <Route
          path="/"
          element={
            <Home
              cart={cart.cart}
              addItem={cart.addItem}
              wishlist={wish.wishlist}
              toggleWish={wish.toggle}
            />
          }
        />

        {/* Canonical Shawls collection */}
        <Route
          path="/shawls"
          element={
            <Category
              routeType="shawls-root"
              addItem={cart.addItem}
              wishlist={wish.wishlist}
              toggleWish={wish.toggle}
            />
          }
        />

        {/* Existing /shawls/... URLs kept for compatibility and SEO */}
        <Route
          path="/shawls/:category"
          element={
            <Category
              routeType="legacy"
              addItem={cart.addItem}
              wishlist={wish.wishlist}
              toggleWish={wish.toggle}
            />
          }
        />

        {/* Generic top-level collections, e.g. /collection/kaleen */}
        <Route
          path="/collection/:category"
          element={
            <Category
              routeType="collection"
              addItem={cart.addItem}
              wishlist={wish.wishlist}
              toggleWish={wish.toggle}
            />
          }
        />

  {/* Generic top-level collections, e.g. /collection/kaleen */}
        <Route
          path="/collection/:category"
          element={
            <Category
              routeType="collection"
              addItem={cart.addItem}
              wishlist={wish.wishlist}
              toggleWish={wish.toggle}
            />
          }
        />

        {/* Products */}
        <Route
          path="/product/:id"
          element={<ProductDetail addItem={cart.addItem} />}
        />

        {/* Checkout */}
        <Route
          path="/checkout"
          element={
            <Checkout
              cart={cart.cart}
              updateQty={cart.updateQty}
              removeItem={cart.removeItem}
              clearCart={cart.clear}
              user={auth.user}
            />
          }
        />

        {/* Informational pages */}
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/pages/:slug" element={<StaticPage />} />

        {/* Authentication */}
        <Route
          path="/login"
          element={<Login onLogin={auth.setUser} />}
        />
        <Route
          path="/signup"
          element={<Signup onLogin={auth.setUser} />}
        />
        <Route
          path="/forgot-password"
          element={<ForgotPassword />}
        />
        <Route
          path="/reset-password"
          element={<ResetPassword />}
        />
        <Route
          path="/verify-email"
          element={<VerifyEmail />}
        />
        <Route
          path="/auth/callback"
          element={<AuthCallback onLogin={auth.setUser} />}
        />

        {/* Customer account */}
        <Route
          path="/account"
          element={
            auth.user ? (
              <Account
                user={auth.user}
                setUser={auth.setUser}
                logout={auth.logout}
                wishlistIds={wish.wishlist}
                toggleWishLocal={wish.toggle}
              />
            ) : (
              <Login onLogin={auth.setUser} />
            )
          }
        />

        {/* Admin */}
        <Route
          path="/admin/*"
          element={
            <AdminApp
              user={auth.user}
              logout={auth.logout}
            />
          }
        />
      </Routes>

      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <Chatbot />}

      <Toaster
        position="bottom-right"
        richColors
      />
    </>
  );
}

function AppRouter() {
  return <Shell />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}

